/**
 * DOC — Report Writer Base-Tables lookup maintenance pages (§10 core surface):
 *   • Format Masks   — doc_format_mask   (+ _tl)  — mask_kind enum
 *   • Page Sizes     — doc_page_size     (+ _tl)  — width/height pt
 *   • Static Resources — doc_resource    (+ _tl)  — resource_kind enum
 *
 * Each page follows the platform new-page contract (§17): self-wraps
 * PersonalizationProvider, exposes a PersonalizationToggle, renders its list via
 * PersonalizedGrid, filters with free-text + FacetFilter + ClearFiltersButton, and
 * edits in a centered Modal with a TranslationsEditor writing one `_tl` row per
 * active locale. CHECK-enum values render via a single labelled-constant per enum
 * (never the raw stored literal); Y/N via <YesNoBadge>.
 */
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { buildTranslationsGroup } from '../../components/common/translations_group';
import { apiDb } from '../../lib/api_client';
import { routedInsert, routedUpdate } from '../../lib/routed_writes';
import { useActiveLocale } from '../../lib/use_active_locale';
import type { AuthSession } from '../../types/auth';
import { Modal } from '../../components/common/Modal';
import { EmptyState } from '../../components/common/EmptyState';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { YesNoBadge } from '../../components/common/YesNoBadge';
import { TranslationsEditor } from '../../components/common/TranslationsEditor';
import type { TranslationRow } from '../../components/common/TranslationsEditor';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, PersonalizedField, GRID_TABLE_CLASS } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';
import { hasAccess } from '../../types/auth';

const inputCls = 'mb-3 h-8 w-full rounded border border-csm-border px-2 text-csm-sm';
const labelCls = 'mb-1 block text-csm-sm font-medium text-csm-ink-soft';
const btnCls = 'h-7 rounded border border-csm-border bg-white px-2 text-csm-sm hover:bg-csm-surface-alt';
const primaryBtnCls = 'h-7 rounded border border-primary bg-primary px-2 text-csm-sm text-white hover:bg-primary-hover';

// ── CHECK-enum labelled constants (§17.B11 / §10 i18n conformance) ────────────
// Stored value stays the code; the label is localized through i18n and is the
// ONLY thing rendered in cells, facets and select options.
interface EnumOpt { value: string; labelKey: string; fallback: string; }

const MASK_KINDS: EnumOpt[] = [
  { value: 'NUMBER',   labelKey: 'doc.mask_kind.NUMBER',   fallback: 'Number' },
  { value: 'CURRENCY', labelKey: 'doc.mask_kind.CURRENCY', fallback: 'Currency' },
  { value: 'PERCENT',  labelKey: 'doc.mask_kind.PERCENT',  fallback: 'Percent' },
  { value: 'DATE',     labelKey: 'doc.mask_kind.DATE',     fallback: 'Date' },
  { value: 'PHONE',    labelKey: 'doc.mask_kind.PHONE',    fallback: 'Phone' },
  { value: 'TEXT',     labelKey: 'doc.mask_kind.TEXT',     fallback: 'Text' },
];

const RESOURCE_KINDS: EnumOpt[] = [
  { value: 'IMAGE',        labelKey: 'doc.resource_kind.IMAGE',        fallback: 'Image' },
  { value: 'TEXT_SNIPPET', labelKey: 'doc.resource_kind.TEXT_SNIPPET', fallback: 'Text Snippet' },
];

/** Resolve a `_tl` list to the active-locale display_text with EN_US fallback. */
function tlName(tl: { locale_code: string; display_text: string }[], loc: string): string {
  return tl.find(x => x.locale_code === loc)?.display_text
    ?? tl.find(x => x.locale_code === 'EN_US')?.display_text
    ?? '';
}

/* ══════════════════════════════════════════════════════════════════════════ */
/*  FORMAT MASKS                                                                */
/* ══════════════════════════════════════════════════════════════════════════ */

const FORMAT_MASK_PAGE = 'DOC.FORMAT_MASKS';

interface FormatMaskRow {
  format_mask_code: string;
  mask_kind: string;
  is_active: 'Y' | 'N';
  sort_order: number;
  tl: { locale_code: string; display_text: string }[];
}

const FORMAT_MASK_COLS: GridColumnDef[] = [
  { key: 'format_mask_code', header: 'Code', defaultWidth: 180 },
  { key: 'mask_kind',        header: 'Kind', defaultWidth: 140 },
  { key: 'display_text',     header: 'Name', defaultWidth: 260 },
  { key: 'is_active',        header: 'Active', defaultWidth: 90 },
  { key: 'sort_order',       header: 'Sort', defaultWidth: 80 },
];

function FormatMaskInner({ session }: { session: AuthSession }) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canAdd = hasAccess(session, FORMAT_MASK_PAGE, 'add');
  const canChange = hasAccess(session, FORMAT_MASK_PAGE, 'change');

  const { t } = useTranslation();
  const locale = useActiveLocale();
  const detail = useExpandedRows<string>();
  const [rows, setRows] = useState<FormatMaskRow[]>([]);
  const [filter, setFilter] = useState('');
  const [kindFacet, setKindFacet] = useState<string[]>([]);
  // §17.B12: Active facet defaults to Active-only on first visit.
  const [activeFacet, setActiveFacet] = useState<string[]>(['Y']);
  const [editing, setEditing] = useState<FormatMaskRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const kindLabel = (v: string) => {
    const o = MASK_KINDS.find(k => k.value === v);
    return o ? t(o.labelKey, o.fallback) : v;
  };

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data, error: e } = await apiDb.from('doc_format_mask').select(`
        format_mask_code, mask_kind, is_active, sort_order,
        doc_format_mask_tl ( locale_code, display_text )
      `).order('sort_order');
      if (e) throw e;
      setRows(((data ?? []) as unknown as (Omit<FormatMaskRow, 'tl'> & { doc_format_mask_tl: FormatMaskRow['tl'] })[])
        .map(r => ({ ...r, tl: r.doc_format_mask_tl ?? [] })));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { void reload(); }, []);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter(r =>
      (!q || r.format_mask_code.toLowerCase().includes(q) || tlName(r.tl, locale).toLowerCase().includes(q)) &&
      (kindFacet.length === 0 || kindFacet.includes(r.mask_kind)) &&
      (activeFacet.length === 0 || activeFacet.includes(r.is_active))
    );
  }, [rows, filter, kindFacet, activeFacet, locale]);

  const anyFilter = filter.trim() !== '' || kindFacet.length > 0 || activeFacet.length > 0;

  // §17.B13 related-children: flat lookup, no child collections.
  // TODO: wire relations if this lookup ever gains dependents.
  const buildRelatedGroups = (code: string): RelatedChildGroup[] => [
    buildTranslationsGroup(rows.find(r => r.format_mask_code === code)?.tl, t, locale),
  ];
  const hasRelated = (code: string) => buildRelatedGroups(code).some(g => g.items.length > 0);

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-10 shrink-0 items-center gap-2 border-b border-csm-border bg-white px-3">
        <span className="text-csm-sm font-semibold uppercase tracking-wider text-csm-ink-soft">{t('doc.format_masks.title', 'Format Masks')}</span>
        <input
          type="text"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder={t('common.filter', 'Filter…')}
          className="h-7 w-44 rounded border border-csm-border px-2 text-csm-sm focus:border-csm-watermark focus:outline-none"
        />
        <FacetFilter
          label={t('doc.format_masks.facet.kind', 'Kind')}
          options={MASK_KINDS.map(k => ({ value: k.value, label: t(k.labelKey, k.fallback) }))}
          selected={kindFacet}
          onChange={setKindFacet}
        />
        <FacetFilter
          label={t('common.boolean.active', 'Active')}
          options={[
            { value: 'Y', label: t('common.boolean.active', 'Active') },
            { value: 'N', label: t('common.boolean.inactive', 'Inactive') },
          ]}
          selected={activeFacet}
          onChange={setActiveFacet}
        />
        <ClearFiltersButton
          active={anyFilter}
          onClear={() => { setFilter(''); setKindFacet([]); setActiveFacet([]); }}
        />
        {canAdd && (<button onClick={() => setCreating(true)} className={primaryBtnCls}>+ {t('common.new', 'New')}</button>)}
        <button onClick={() => void reload()} className={btnCls}>{t('common.refresh', 'Refresh')}</button>
        {busy && <span className="text-csm-xs text-csm-watermark">{t('common.loading', 'Loading…')}</span>}
        {error && <span className="text-csm-xs text-csm-danger">{error}</span>}
        <div className="ml-auto"><PersonalizationToggle /></div>
      </div>

      <div className="flex-1 overflow-auto">
        <PersonalizedGrid componentKey="grid" columns={FORMAT_MASK_COLS}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="sticky top-0 z-10 bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="w-7 border-b border-csm-border px-1 py-1.5" title={t('common.related.header', 'Related records')} />
                  {visibleCols.map(col => (
                    <th key={col.key} className="border-b border-csm-border px-3 py-1.5 text-left"
                      style={widths[col.key] ? { width: widths[col.key] } : undefined}>{col.header}</th>
                  ))}
                  <th className="border-b border-csm-border px-3 py-1.5 text-left" style={{ width: '1%' }}>{t('common.actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <Fragment key={r.format_mask_code}>
                  <tr className="hover:bg-csm-surface-alt">
                    <td className="border-b border-csm-border-soft px-1 py-1.5 text-center align-middle" onClick={e => e.stopPropagation()}>
                      <RelatedExpandButton
                        open={detail.isOpen(r.format_mask_code)}
                        hasChildren={hasRelated(r.format_mask_code)}
                        onToggle={() => detail.toggle(r.format_mask_code)}
                        expandLabel={t('common.related.show', 'Show related records')}
                        collapseLabel={t('common.related.hide', 'Hide related')}
                        tooltip={t('common.related.tooltip', 'Show related records')}
                      />
                    </td>
                    {visibleCols.map(col => (
                      <td key={col.key} className="border-b border-csm-border-soft px-3 py-1.5"
                        style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                        {col.key === 'format_mask_code' && <span className="font-mono text-csm-sm">{r.format_mask_code}</span>} {/* fk-display-ok: natural lookup code */}
                        {col.key === 'mask_kind'        && kindLabel(r.mask_kind)}
                        {col.key === 'display_text'     && tlName(r.tl, locale)}
                        {col.key === 'is_active'        && <YesNoBadge value={r.is_active} yesLabel={t('common.boolean.active', 'Active')} noLabel={t('common.boolean.inactive', 'Inactive')} />}
                        {col.key === 'sort_order'       && r.sort_order}
                      </td>
                    ))}
                    <td className="border-b border-csm-border-soft px-3 py-1.5 text-right">
                      {canChange && (<button onClick={() => setEditing(r)} className="rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-border-soft">{t('common.edit', 'Edit')}</button>)}
                    </td>
                  </tr>
                  {detail.isOpen(r.format_mask_code) && (
                    <tr>
                      <td colSpan={visibleCols.length + 2} className="border-b border-csm-border-soft bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}>
                        <RelatedChildrenPanel groups={buildRelatedGroups(r.format_mask_code)} session={session} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
                {filtered.length === 0 && !busy && (
                  <tr><td colSpan={visibleCols.length + 2} className="py-12 text-center"><EmptyState title={t('common.no_records', 'No records')} /></td></tr>
                )}
              </tbody>
            </table>
          )}
        </PersonalizedGrid>
      </div>

      {(editing || creating) && (
        <FormatMaskModal
          session={session}
          row={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); void reload(); }}
        />
      )}
    </div>
  );
}

function FormatMaskModal({ session, row, onClose, onSaved }: {
  session: AuthSession; row: FormatMaskRow | null; onClose: () => void; onSaved: () => void;
}) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canChange = hasAccess(session, FORMAT_MASK_PAGE, 'change');

  const { t } = useTranslation();
  const isCreate = row === null;
  const [code, setCode] = useState(row?.format_mask_code ?? '');
  const [kind, setKind] = useState(row?.mask_kind ?? 'NUMBER');
  const [sortOrder, setSortOrder] = useState(String(row?.sort_order ?? 0));
  const [isActive, setIsActive] = useState(!row || row.is_active === 'Y');
  const [translations, setTranslations] = useState<TranslationRow[]>(
    row?.tl && row.tl.length > 0 ? row.tl : []
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      if (!code.trim()) throw new Error(t('common.err.code_required', 'Code is required.'));
      if (!translations.some(tr => tr.display_text?.trim())) throw new Error(t('common.err.translation_required', 'At least one translation is required.'));
      const pk = code.trim();

      if (row === null) {
        const { error: e } = await routedInsert(session, 'doc_format_mask', {
          format_mask_code: pk, mask_kind: kind, sort_order: Number(sortOrder), is_active: isActive ? 'Y' : 'N',
        });
        if (e) throw new Error(e.message);
      } else {
        const { error: e } = await routedUpdate(session, 'doc_format_mask', 'format_mask_code', row.format_mask_code, {
          mask_kind: kind, sort_order: Number(sortOrder), is_active: isActive ? 'Y' : 'N',
        });
        if (e) throw new Error(e.message);
      }
      const target = isCreate ? pk : row.format_mask_code;
      for (const tr of translations.filter(x => x.display_text?.trim())) {
        // §8/§2: _tl composite-PK companions are the sanctioned direct-write
        // exception (see check_write_path.mjs) — routedInsert rejected the
        // unregistered table. Upsert so create AND edit persist.
        const { error: te } = await apiDb.from('doc_format_mask_tl').upsert({
          format_mask_code: target, locale_code: tr.locale_code, display_text: tr.display_text,
        }, { onConflict: 'format_mask_code,locale_code' });
        if (te) throw new Error(te.message);
      }
      onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={isCreate ? t('doc.format_masks.new', 'New Format Mask') : `${t('common.edit', 'Edit')} — ${row?.format_mask_code}`}
      onClose={onClose}
      width="md"
      headerAction={<PersonalizationToggle />}
      footer={
        <>
          <button onClick={onClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">{t('common.cancel', 'Cancel')}</button>
          {canChange && (<button onClick={() => void save()} disabled={busy} className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">{busy ? t('common.saving', 'Saving…') : t('common.save', 'Save')}</button>)}
        </>
      }
    >
      <PersonalizedField componentKey="modal" fieldKey="format_mask_code" label={t('common.code', 'Code')}>
        <label className={labelCls}>{t('common.code', 'Code')}</label>
        <input type="text" value={code} onChange={e => setCode(e.target.value)} disabled={!isCreate} placeholder="CURRENCY_USD" className={inputCls} />
      </PersonalizedField>

      <PersonalizedField componentKey="modal" fieldKey="mask_kind" label={t('doc.format_masks.field.kind', 'Kind')}>
        <label className={labelCls}>{t('doc.format_masks.field.kind', 'Kind')}</label>
        <select value={kind} onChange={e => setKind(e.target.value)} className={inputCls}>
          {MASK_KINDS.map(k => <option key={k.value} value={k.value}>{t(k.labelKey, k.fallback)}</option>)}
        </select>
      </PersonalizedField>

      <PersonalizedField componentKey="modal" fieldKey="sort_order" label={t('common.sort_order', 'Sort Order')}>
        <label className={labelCls}>{t('common.sort_order', 'Sort Order')}</label>
        <input type="number" value={sortOrder} onChange={e => setSortOrder(e.target.value)} className={inputCls} />
      </PersonalizedField>

      <PersonalizedField componentKey="modal" fieldKey="is_active" label={t('common.boolean.active', 'Active')}>
        <label className="mb-3 flex items-center gap-2 text-csm-sm text-csm-ink-soft">
          <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} className="h-4 w-4 accent-gray-700" />
          {t('common.boolean.active', 'Active')}
        </label>
      </PersonalizedField>

      <TranslationsEditor value={translations} onChange={setTranslations} label={t('common.translations_lbl', 'Translations')} />

      {error && <div className="mt-3 rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-sm text-csm-danger">{error}</div>}
    </Modal>
  );
}

export function DocFormatMaskPage({ session }: { session: AuthSession }) {
  return (
    <PersonalizationProvider pageCode={FORMAT_MASK_PAGE} session={session}>
      <FormatMaskInner session={session} />
    </PersonalizationProvider>
  );
}

/* ══════════════════════════════════════════════════════════════════════════ */
/*  PAGE SIZES                                                                  */
/* ══════════════════════════════════════════════════════════════════════════ */

const PAGE_SIZE_PAGE = 'DOC.PAGE_SIZES';

interface PageSizeRow {
  page_size_code: string;
  width_pt: number;
  height_pt: number;
  is_active: 'Y' | 'N';
  sort_order: number;
  tl: { locale_code: string; display_text: string }[];
}

const PAGE_SIZE_COLS: GridColumnDef[] = [
  { key: 'page_size_code', header: 'Code', defaultWidth: 160 },
  { key: 'width_pt',       header: 'Width (pt)', defaultWidth: 120 },
  { key: 'height_pt',      header: 'Height (pt)', defaultWidth: 120 },
  { key: 'display_text',   header: 'Name', defaultWidth: 240 },
  { key: 'is_active',      header: 'Active', defaultWidth: 90 },
  { key: 'sort_order',     header: 'Sort', defaultWidth: 80 },
];

function PageSizeInner({ session }: { session: AuthSession }) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canAdd = hasAccess(session, PAGE_SIZE_PAGE, 'add');
  const canChange = hasAccess(session, PAGE_SIZE_PAGE, 'change');

  const { t } = useTranslation();
  const locale = useActiveLocale();
  const detail = useExpandedRows<string>();
  const [rows, setRows] = useState<PageSizeRow[]>([]);
  const [filter, setFilter] = useState('');
  // §17.B12: Active facet defaults to Active-only on first visit.
  const [activeFacet, setActiveFacet] = useState<string[]>(['Y']);
  const [editing, setEditing] = useState<PageSizeRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data, error: e } = await apiDb.from('doc_page_size').select(`
        page_size_code, width_pt, height_pt, is_active, sort_order,
        doc_page_size_tl ( locale_code, display_text )
      `).order('sort_order');
      if (e) throw e;
      setRows(((data ?? []) as unknown as (Omit<PageSizeRow, 'tl'> & { doc_page_size_tl: PageSizeRow['tl'] })[])
        .map(r => ({ ...r, tl: r.doc_page_size_tl ?? [] })));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { void reload(); }, []);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter(r =>
      (!q || r.page_size_code.toLowerCase().includes(q) || tlName(r.tl, locale).toLowerCase().includes(q)) &&
      (activeFacet.length === 0 || activeFacet.includes(r.is_active))
    );
  }, [rows, filter, activeFacet, locale]);

  const anyFilter = filter.trim() !== '' || activeFacet.length > 0;

  // §17.B13 related-children: flat lookup, no child collections.
  // TODO: wire relations if this lookup ever gains dependents.
  const buildRelatedGroups = (code: string): RelatedChildGroup[] => [
    buildTranslationsGroup(rows.find(r => r.page_size_code === code)?.tl, t, locale),
  ];
  const hasRelated = (code: string) => buildRelatedGroups(code).some(g => g.items.length > 0);

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-10 shrink-0 items-center gap-2 border-b border-csm-border bg-white px-3">
        <span className="text-csm-sm font-semibold uppercase tracking-wider text-csm-ink-soft">{t('doc.page_sizes.title', 'Page Sizes')}</span>
        <input
          type="text"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder={t('common.filter', 'Filter…')}
          className="h-7 w-44 rounded border border-csm-border px-2 text-csm-sm focus:border-csm-watermark focus:outline-none"
        />
        <FacetFilter
          label={t('common.boolean.active', 'Active')}
          options={[
            { value: 'Y', label: t('common.boolean.active', 'Active') },
            { value: 'N', label: t('common.boolean.inactive', 'Inactive') },
          ]}
          selected={activeFacet}
          onChange={setActiveFacet}
        />
        <ClearFiltersButton active={anyFilter} onClear={() => { setFilter(''); setActiveFacet([]); }} />
        {canAdd && (<button onClick={() => setCreating(true)} className={primaryBtnCls}>+ {t('common.new', 'New')}</button>)}
        <button onClick={() => void reload()} className={btnCls}>{t('common.refresh', 'Refresh')}</button>
        {busy && <span className="text-csm-xs text-csm-watermark">{t('common.loading', 'Loading…')}</span>}
        {error && <span className="text-csm-xs text-csm-danger">{error}</span>}
        <div className="ml-auto"><PersonalizationToggle /></div>
      </div>

      <div className="flex-1 overflow-auto">
        <PersonalizedGrid componentKey="grid" columns={PAGE_SIZE_COLS}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="sticky top-0 z-10 bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="w-7 border-b border-csm-border px-1 py-1.5" title={t('common.related.header', 'Related records')} />
                  {visibleCols.map(col => (
                    <th key={col.key} className="border-b border-csm-border px-3 py-1.5 text-left"
                      style={widths[col.key] ? { width: widths[col.key] } : undefined}>{col.header}</th>
                  ))}
                  <th className="border-b border-csm-border px-3 py-1.5 text-left" style={{ width: '1%' }}>{t('common.actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <Fragment key={r.page_size_code}>
                  <tr className="hover:bg-csm-surface-alt">
                    <td className="border-b border-csm-border-soft px-1 py-1.5 text-center align-middle" onClick={e => e.stopPropagation()}>
                      <RelatedExpandButton
                        open={detail.isOpen(r.page_size_code)}
                        hasChildren={hasRelated(r.page_size_code)}
                        onToggle={() => detail.toggle(r.page_size_code)}
                        expandLabel={t('common.related.show', 'Show related records')}
                        collapseLabel={t('common.related.hide', 'Hide related')}
                        tooltip={t('common.related.tooltip', 'Show related records')}
                      />
                    </td>
                    {visibleCols.map(col => (
                      <td key={col.key} className="border-b border-csm-border-soft px-3 py-1.5"
                        style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                        {col.key === 'page_size_code' && <span className="font-mono text-csm-sm">{r.page_size_code}</span>} {/* fk-display-ok: natural lookup code */}
                        {col.key === 'width_pt'       && r.width_pt}
                        {col.key === 'height_pt'      && r.height_pt}
                        {col.key === 'display_text'   && tlName(r.tl, locale)}
                        {col.key === 'is_active'      && <YesNoBadge value={r.is_active} yesLabel={t('common.boolean.active', 'Active')} noLabel={t('common.boolean.inactive', 'Inactive')} />}
                        {col.key === 'sort_order'     && r.sort_order}
                      </td>
                    ))}
                    <td className="border-b border-csm-border-soft px-3 py-1.5 text-right">
                      {canChange && (<button onClick={() => setEditing(r)} className="rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-border-soft">{t('common.edit', 'Edit')}</button>)}
                    </td>
                  </tr>
                  {detail.isOpen(r.page_size_code) && (
                    <tr>
                      <td colSpan={visibleCols.length + 2} className="border-b border-csm-border-soft bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}>
                        <RelatedChildrenPanel groups={buildRelatedGroups(r.page_size_code)} session={session} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
                {filtered.length === 0 && !busy && (
                  <tr><td colSpan={visibleCols.length + 2} className="py-12 text-center"><EmptyState title={t('common.no_records', 'No records')} /></td></tr>
                )}
              </tbody>
            </table>
          )}
        </PersonalizedGrid>
      </div>

      {(editing || creating) && (
        <PageSizeModal
          session={session}
          row={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); void reload(); }}
        />
      )}
    </div>
  );
}

function PageSizeModal({ session, row, onClose, onSaved }: {
  session: AuthSession; row: PageSizeRow | null; onClose: () => void; onSaved: () => void;
}) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canChange = hasAccess(session, PAGE_SIZE_PAGE, 'change');

  const { t } = useTranslation();
  const isCreate = row === null;
  const [code, setCode] = useState(row?.page_size_code ?? '');
  const [width, setWidth] = useState(String(row?.width_pt ?? ''));
  const [height, setHeight] = useState(String(row?.height_pt ?? ''));
  const [sortOrder, setSortOrder] = useState(String(row?.sort_order ?? 0));
  const [isActive, setIsActive] = useState(!row || row.is_active === 'Y');
  const [translations, setTranslations] = useState<TranslationRow[]>(
    row?.tl && row.tl.length > 0 ? row.tl : []
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      if (!code.trim()) throw new Error(t('common.err.code_required', 'Code is required.'));
      if (!width.trim() || !height.trim()) throw new Error(t('doc.page_sizes.err.dimensions', 'Width and height (pt) are required.'));
      if (!translations.some(tr => tr.display_text?.trim())) throw new Error(t('common.err.translation_required', 'At least one translation is required.'));
      const pk = code.trim();

      if (row === null) {
        const { error: e } = await routedInsert(session, 'doc_page_size', {
          page_size_code: pk, width_pt: Number(width), height_pt: Number(height), sort_order: Number(sortOrder), is_active: isActive ? 'Y' : 'N',
        });
        if (e) throw new Error(e.message);
      } else {
        const { error: e } = await routedUpdate(session, 'doc_page_size', 'page_size_code', row.page_size_code, {
          width_pt: Number(width), height_pt: Number(height), sort_order: Number(sortOrder), is_active: isActive ? 'Y' : 'N',
        });
        if (e) throw new Error(e.message);
      }
      const target = isCreate ? pk : row.page_size_code;
      for (const tr of translations.filter(x => x.display_text?.trim())) {
        // §8/§2: _tl composite-PK companions are the sanctioned direct-write
        // exception (see check_write_path.mjs) — routedInsert rejected the
        // unregistered table. Upsert so create AND edit persist.
        const { error: te } = await apiDb.from('doc_page_size_tl').upsert({
          page_size_code: target, locale_code: tr.locale_code, display_text: tr.display_text,
        }, { onConflict: 'page_size_code,locale_code' });
        if (te) throw new Error(te.message);
      }
      onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={isCreate ? t('doc.page_sizes.new', 'New Page Size') : `${t('common.edit', 'Edit')} — ${row?.page_size_code}`}
      onClose={onClose}
      width="md"
      headerAction={<PersonalizationToggle />}
      footer={
        <>
          <button onClick={onClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">{t('common.cancel', 'Cancel')}</button>
          {canChange && (<button onClick={() => void save()} disabled={busy} className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">{busy ? t('common.saving', 'Saving…') : t('common.save', 'Save')}</button>)}
        </>
      }
    >
      <PersonalizedField componentKey="modal" fieldKey="page_size_code" label={t('common.code', 'Code')}>
        <label className={labelCls}>{t('common.code', 'Code')}</label>
        <input type="text" value={code} onChange={e => setCode(e.target.value)} disabled={!isCreate} placeholder="A4" className={inputCls} />
      </PersonalizedField>

      <PersonalizedField componentKey="modal" fieldKey="width_pt" label={t('doc.page_sizes.field.width', 'Width (pt)')}>
        <label className={labelCls}>{t('doc.page_sizes.field.width', 'Width (pt)')}</label>
        <input type="number" value={width} onChange={e => setWidth(e.target.value)} placeholder="595.28" className={inputCls} />
      </PersonalizedField>

      <PersonalizedField componentKey="modal" fieldKey="height_pt" label={t('doc.page_sizes.field.height', 'Height (pt)')}>
        <label className={labelCls}>{t('doc.page_sizes.field.height', 'Height (pt)')}</label>
        <input type="number" value={height} onChange={e => setHeight(e.target.value)} placeholder="841.89" className={inputCls} />
      </PersonalizedField>

      <PersonalizedField componentKey="modal" fieldKey="sort_order" label={t('common.sort_order', 'Sort Order')}>
        <label className={labelCls}>{t('common.sort_order', 'Sort Order')}</label>
        <input type="number" value={sortOrder} onChange={e => setSortOrder(e.target.value)} className={inputCls} />
      </PersonalizedField>

      <PersonalizedField componentKey="modal" fieldKey="is_active" label={t('common.boolean.active', 'Active')}>
        <label className="mb-3 flex items-center gap-2 text-csm-sm text-csm-ink-soft">
          <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} className="h-4 w-4 accent-gray-700" />
          {t('common.boolean.active', 'Active')}
        </label>
      </PersonalizedField>

      <TranslationsEditor value={translations} onChange={setTranslations} label={t('common.translations_lbl', 'Translations')} />

      {error && <div className="mt-3 rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-sm text-csm-danger">{error}</div>}
    </Modal>
  );
}

export function DocPageSizePage({ session }: { session: AuthSession }) {
  return (
    <PersonalizationProvider pageCode={PAGE_SIZE_PAGE} session={session}>
      <PageSizeInner session={session} />
    </PersonalizationProvider>
  );
}

/* ══════════════════════════════════════════════════════════════════════════ */
/*  STATIC RESOURCES                                                            */
/* ══════════════════════════════════════════════════════════════════════════ */

const RESOURCE_PAGE = 'DOC.RESOURCES';

interface ResourceRow {
  resource_code: string;
  resource_kind: string;
  text_content: string | null;
  is_active: 'Y' | 'N';
  sort_order: number;
  tl: { locale_code: string; display_text: string }[];
}

const RESOURCE_COLS: GridColumnDef[] = [
  { key: 'resource_code', header: 'Code', defaultWidth: 200 },
  { key: 'resource_kind', header: 'Kind', defaultWidth: 150 },
  { key: 'display_text',  header: 'Name', defaultWidth: 260 },
  { key: 'is_active',     header: 'Active', defaultWidth: 90 },
  { key: 'sort_order',    header: 'Sort', defaultWidth: 80 },
];

function ResourceInner({ session }: { session: AuthSession }) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canAdd = hasAccess(session, RESOURCE_PAGE, 'add');
  const canChange = hasAccess(session, RESOURCE_PAGE, 'change');

  const { t } = useTranslation();
  const locale = useActiveLocale();
  const detail = useExpandedRows<string>();
  const [rows, setRows] = useState<ResourceRow[]>([]);
  const [filter, setFilter] = useState('');
  const [kindFacet, setKindFacet] = useState<string[]>([]);
  // §17.B12: Active facet defaults to Active-only on first visit.
  const [activeFacet, setActiveFacet] = useState<string[]>(['Y']);
  const [editing, setEditing] = useState<ResourceRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const kindLabel = (v: string) => {
    const o = RESOURCE_KINDS.find(k => k.value === v);
    return o ? t(o.labelKey, o.fallback) : v;
  };

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data, error: e } = await apiDb.from('doc_resource').select(`
        resource_code, resource_kind, text_content, is_active, sort_order,
        doc_resource_tl ( locale_code, display_text )
      `).order('sort_order');
      if (e) throw e;
      setRows(((data ?? []) as unknown as (Omit<ResourceRow, 'tl'> & { doc_resource_tl: ResourceRow['tl'] })[])
        .map(r => ({ ...r, tl: r.doc_resource_tl ?? [] })));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { void reload(); }, []);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter(r =>
      (!q || r.resource_code.toLowerCase().includes(q) || tlName(r.tl, locale).toLowerCase().includes(q)) &&
      (kindFacet.length === 0 || kindFacet.includes(r.resource_kind)) &&
      (activeFacet.length === 0 || activeFacet.includes(r.is_active))
    );
  }, [rows, filter, kindFacet, activeFacet, locale]);

  const anyFilter = filter.trim() !== '' || kindFacet.length > 0 || activeFacet.length > 0;

  // §17.B13 related-children: flat lookup, no child collections.
  // TODO: wire relations if this lookup ever gains dependents.
  const buildRelatedGroups = (code: string): RelatedChildGroup[] => [
    buildTranslationsGroup(rows.find(r => r.resource_code === code)?.tl, t, locale),
  ];
  const hasRelated = (code: string) => buildRelatedGroups(code).some(g => g.items.length > 0);

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-10 shrink-0 items-center gap-2 border-b border-csm-border bg-white px-3">
        <span className="text-csm-sm font-semibold uppercase tracking-wider text-csm-ink-soft">{t('doc.resources.title', 'Static Resources')}</span>
        <input
          type="text"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder={t('common.filter', 'Filter…')}
          className="h-7 w-44 rounded border border-csm-border px-2 text-csm-sm focus:border-csm-watermark focus:outline-none"
        />
        <FacetFilter
          label={t('doc.resources.facet.kind', 'Kind')}
          options={RESOURCE_KINDS.map(k => ({ value: k.value, label: t(k.labelKey, k.fallback) }))}
          selected={kindFacet}
          onChange={setKindFacet}
        />
        <FacetFilter
          label={t('common.boolean.active', 'Active')}
          options={[
            { value: 'Y', label: t('common.boolean.active', 'Active') },
            { value: 'N', label: t('common.boolean.inactive', 'Inactive') },
          ]}
          selected={activeFacet}
          onChange={setActiveFacet}
        />
        <ClearFiltersButton active={anyFilter} onClear={() => { setFilter(''); setKindFacet([]); setActiveFacet([]); }} />
        {canAdd && (<button onClick={() => setCreating(true)} className={primaryBtnCls}>+ {t('common.new', 'New')}</button>)}
        <button onClick={() => void reload()} className={btnCls}>{t('common.refresh', 'Refresh')}</button>
        {busy && <span className="text-csm-xs text-csm-watermark">{t('common.loading', 'Loading…')}</span>}
        {error && <span className="text-csm-xs text-csm-danger">{error}</span>}
        <div className="ml-auto"><PersonalizationToggle /></div>
      </div>

      <div className="flex-1 overflow-auto">
        <PersonalizedGrid componentKey="grid" columns={RESOURCE_COLS}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="sticky top-0 z-10 bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="w-7 border-b border-csm-border px-1 py-1.5" title={t('common.related.header', 'Related records')} />
                  {visibleCols.map(col => (
                    <th key={col.key} className="border-b border-csm-border px-3 py-1.5 text-left"
                      style={widths[col.key] ? { width: widths[col.key] } : undefined}>{col.header}</th>
                  ))}
                  <th className="border-b border-csm-border px-3 py-1.5 text-left" style={{ width: '1%' }}>{t('common.actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <Fragment key={r.resource_code}>
                  <tr className="hover:bg-csm-surface-alt">
                    <td className="border-b border-csm-border-soft px-1 py-1.5 text-center align-middle" onClick={e => e.stopPropagation()}>
                      <RelatedExpandButton
                        open={detail.isOpen(r.resource_code)}
                        hasChildren={hasRelated(r.resource_code)}
                        onToggle={() => detail.toggle(r.resource_code)}
                        expandLabel={t('common.related.show', 'Show related records')}
                        collapseLabel={t('common.related.hide', 'Hide related')}
                        tooltip={t('common.related.tooltip', 'Show related records')}
                      />
                    </td>
                    {visibleCols.map(col => (
                      <td key={col.key} className="border-b border-csm-border-soft px-3 py-1.5"
                        style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                        {col.key === 'resource_code' && <span className="font-mono text-csm-sm">{r.resource_code}</span>} {/* fk-display-ok: natural lookup code */}
                        {col.key === 'resource_kind' && kindLabel(r.resource_kind)}
                        {col.key === 'display_text'  && tlName(r.tl, locale)}
                        {col.key === 'is_active'     && <YesNoBadge value={r.is_active} yesLabel={t('common.boolean.active', 'Active')} noLabel={t('common.boolean.inactive', 'Inactive')} />}
                        {col.key === 'sort_order'    && r.sort_order}
                      </td>
                    ))}
                    <td className="border-b border-csm-border-soft px-3 py-1.5 text-right">
                      {canChange && (<button onClick={() => setEditing(r)} className="rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-border-soft">{t('common.edit', 'Edit')}</button>)}
                    </td>
                  </tr>
                  {detail.isOpen(r.resource_code) && (
                    <tr>
                      <td colSpan={visibleCols.length + 2} className="border-b border-csm-border-soft bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}>
                        <RelatedChildrenPanel groups={buildRelatedGroups(r.resource_code)} session={session} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
                {filtered.length === 0 && !busy && (
                  <tr><td colSpan={visibleCols.length + 2} className="py-12 text-center"><EmptyState title={t('common.no_records', 'No records')} /></td></tr>
                )}
              </tbody>
            </table>
          )}
        </PersonalizedGrid>
      </div>

      {(editing || creating) && (
        <ResourceModal
          session={session}
          row={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); void reload(); }}
        />
      )}
    </div>
  );
}

function ResourceModal({ session, row, onClose, onSaved }: {
  session: AuthSession; row: ResourceRow | null; onClose: () => void; onSaved: () => void;
}) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canChange = hasAccess(session, RESOURCE_PAGE, 'change');

  const { t } = useTranslation();
  const isCreate = row === null;
  const [code, setCode] = useState(row?.resource_code ?? '');
  const [kind, setKind] = useState(row?.resource_kind ?? 'TEXT_SNIPPET');
  const [textContent, setTextContent] = useState(row?.text_content ?? '');
  const [sortOrder, setSortOrder] = useState(String(row?.sort_order ?? 0));
  const [isActive, setIsActive] = useState(!row || row.is_active === 'Y');
  const [translations, setTranslations] = useState<TranslationRow[]>(
    row?.tl && row.tl.length > 0 ? row.tl : []
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      if (!code.trim()) throw new Error(t('common.err.code_required', 'Code is required.'));
      if (!translations.some(tr => tr.display_text?.trim())) throw new Error(t('common.err.translation_required', 'At least one translation is required.'));
      // The table CHECK requires exactly one payload column per kind; IMAGE payloads
      // (DMS documents) are attached by the designer/import path, so this maintenance
      // screen only authors TEXT_SNIPPET inline content.
      if (kind === 'TEXT_SNIPPET' && !textContent.trim()) throw new Error(t('doc.resources.err.text_required', 'Text content is required for a text snippet.'));
      const pk = code.trim();

      if (row === null) {
        if (kind !== 'TEXT_SNIPPET') throw new Error(t('doc.resources.err.image_via_designer', 'Image resources are added through the designer or an import (they attach a DMS document).'));
        const { error: e } = await routedInsert(session, 'doc_resource', {
          resource_code: pk, resource_kind: kind, text_content: textContent, sort_order: Number(sortOrder), is_active: isActive ? 'Y' : 'N',
        });
        if (e) throw new Error(e.message);
      } else {
        const patch: Record<string, unknown> = { sort_order: Number(sortOrder), is_active: isActive ? 'Y' : 'N' };
        if (row.resource_kind === 'TEXT_SNIPPET') patch.text_content = textContent;
        const { error: e } = await routedUpdate(session, 'doc_resource', 'resource_code', row.resource_code, patch);
        if (e) throw new Error(e.message);
      }
      const target = isCreate ? pk : row.resource_code;
      for (const tr of translations.filter(x => x.display_text?.trim())) {
        // §8/§2: _tl composite-PK companions are the sanctioned direct-write
        // exception (see check_write_path.mjs) — routedInsert rejected the
        // unregistered table. Upsert so create AND edit persist.
        const { error: te } = await apiDb.from('doc_resource_tl').upsert({
          resource_code: target, locale_code: tr.locale_code, display_text: tr.display_text,
        }, { onConflict: 'resource_code,locale_code' });
        if (te) throw new Error(te.message);
      }
      onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const isImage = (isCreate ? kind : row?.resource_kind) === 'IMAGE';

  return (
    <Modal
      title={isCreate ? t('doc.resources.new', 'New Resource') : `${t('common.edit', 'Edit')} — ${row?.resource_code}`}
      onClose={onClose}
      width="md"
      headerAction={<PersonalizationToggle />}
      footer={
        <>
          <button onClick={onClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">{t('common.cancel', 'Cancel')}</button>
          {canChange && (<button onClick={() => void save()} disabled={busy} className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">{busy ? t('common.saving', 'Saving…') : t('common.save', 'Save')}</button>)}
        </>
      }
    >
      <PersonalizedField componentKey="modal" fieldKey="resource_code" label={t('common.code', 'Code')}>
        <label className={labelCls}>{t('common.code', 'Code')}</label>
        <input type="text" value={code} onChange={e => setCode(e.target.value)} disabled={!isCreate} placeholder="ADDRESS_FOOTER" className={inputCls} />
      </PersonalizedField>

      <PersonalizedField componentKey="modal" fieldKey="resource_kind" label={t('doc.resources.field.kind', 'Kind')}>
        <label className={labelCls}>{t('doc.resources.field.kind', 'Kind')}</label>
        <select value={isCreate ? kind : (row?.resource_kind ?? kind)} onChange={e => setKind(e.target.value)} disabled={!isCreate} className={inputCls}>
          {RESOURCE_KINDS.map(k => <option key={k.value} value={k.value}>{t(k.labelKey, k.fallback)}</option>)}
        </select>
      </PersonalizedField>

      {!isImage && (
        <PersonalizedField componentKey="modal" fieldKey="text_content" label={t('doc.resources.field.text_content', 'Text Content')}>
          <label className={labelCls}>{t('doc.resources.field.text_content', 'Text Content')}</label>
          <textarea value={textContent} onChange={e => setTextContent(e.target.value)} rows={4}
            className="mb-3 w-full rounded border border-csm-border px-2 py-1 text-csm-sm" />
        </PersonalizedField>
      )}
      {isImage && (
        <p className="mb-3 text-csm-xs text-csm-muted">{t('doc.resources.image_hint', 'Image resources reference a DMS document, attached through the designer or an import.')}</p>
      )}

      <PersonalizedField componentKey="modal" fieldKey="sort_order" label={t('common.sort_order', 'Sort Order')}>
        <label className={labelCls}>{t('common.sort_order', 'Sort Order')}</label>
        <input type="number" value={sortOrder} onChange={e => setSortOrder(e.target.value)} className={inputCls} />
      </PersonalizedField>

      <PersonalizedField componentKey="modal" fieldKey="is_active" label={t('common.boolean.active', 'Active')}>
        <label className="mb-3 flex items-center gap-2 text-csm-sm text-csm-ink-soft">
          <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} className="h-4 w-4 accent-gray-700" />
          {t('common.boolean.active', 'Active')}
        </label>
      </PersonalizedField>

      <TranslationsEditor value={translations} onChange={setTranslations} label={t('common.translations_lbl', 'Translations')} />

      {error && <div className="mt-3 rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-sm text-csm-danger">{error}</div>}
    </Modal>
  );
}

export function DocResourcePage({ session }: { session: AuthSession }) {
  return (
    <PersonalizationProvider pageCode={RESOURCE_PAGE} session={session}>
      <ResourceInner session={session} />
    </PersonalizationProvider>
  );
}
